import { useUnit } from 'effector-react'
import { useState } from 'react'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  Badge,
  Button,
  Card,
  Dropdown,
  Flex,
  MenuProps,
  Space,
  Typography,
} from 'antd'
import { $userRole, useUserProfile } from 'src/features/user'
import { ArticleItems } from 'src/pages/budget/budgetCategories/components'
import { LinkifiedText } from 'src/pages/budget/components'
import { type ArticleType } from 'src/pages/budget/types'
import {
  openModalDeleteArticle,
  openModalArticle,
} from 'src/pages/budget/budgetCategories/model'
import { openModalArticleItem } from 'src/pages/budget/model'
import {
  getRolesByGuardRules,
  GUARD_RULES,
} from 'src/features/session/constants'
import { useCeoTeam } from 'src/pages/budget/hook'

type PropsType = {
  article: ArticleType
}

export const Article = ({ article }: PropsType) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { Text } = Typography
  const { articleId, number, name, description, items } = article
  const { ceoTeam } = useCeoTeam()
  const userProfile = useUserProfile()
  const userRole = useUnit($userRole)

  const userRoleDeleteArticleSet = new Set(
    getRolesByGuardRules(GUARD_RULES.DELETE_BUDGET_ARTICLE),
  )

  const hasPermissionDelete =
    ceoTeam.includes(userProfile.id) ||
    (userRole
      ? userRole.some((role) => userRoleDeleteArticleSet.has(role))
      : false)

  const itemsMenu: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <Flex
          gap="small"
          onClick={() =>
            openModalArticleItem({
              article,
              mode: 'new',
              articleItem: null,
            })
          }
        >
          <PlusOutlined />
          Добавить новый пункт
        </Flex>
      ),
    },
    {
      key: '2',
      label: (
        <Flex
          gap="small"
          onClick={() => openModalArticle({ article, mode: 'view' })}
        >
          <EyeOutlined />
          Просмотр
        </Flex>
      ),
    },
    {
      key: '3',
      label: (
        <Flex
          gap="small"
          onClick={() => openModalArticle({ article, mode: 'edit' })}
        >
          <EditOutlined />
          Редактирование
        </Flex>
      ),
    },
    {
      key: '',
      label: (
        <>
          {hasPermissionDelete && (
            <Flex
              gap="small"
              style={{ color: 'var(--color-red-60)' }}
              onClick={() => openModalDeleteArticle({ article })}
            >
              <DeleteOutlined />
              Удаление
            </Flex>
          )}
        </>
      ),
    },
  ]

  const toggleExpanded = () => setIsExpanded((value) => !value)

  return (
    <Card
      styles={{
        header: {
          backgroundColor: 'var(--color-blue-20)',
          fontSize: 32,
          height: 80,
        },
        body: isExpanded ? undefined : { display: 'none' },
      }}
      title={
        <Flex
          align="center"
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Свернуть' : 'Развернуть'} статью ${number}. ${name}`}
          justify="space-between"
          onClick={toggleExpanded}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              toggleExpanded()
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Space>
            <span>
              {number}. {name}
            </span>
            <Badge
              count={items?.length ?? 0}
              showZero
              color="white"
              style={{ color: 'black', marginTop: 5, borderRadius: '4px' }}
            />
          </Space>
          <div onClick={(event) => event.stopPropagation()}>
            <Dropdown
              menu={{ items: itemsMenu }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </div>
        </Flex>
      }
    >
      {isExpanded && (
        <>
          <Text style={{ fontSize: 18 }}>
            <LinkifiedText text={description} />
          </Text>
          <br />
          <br />
          {article.items.length > 0 && (
            <Flex vertical gap={24}>
              <Text style={{ fontSize: 20 }}>Пункты</Text>
              <ArticleItems key={articleId} article={article} />
            </Flex>
          )}
        </>
      )}
    </Card>
  )
}
